/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/jsx-props-no-spreading */
/**
 * @fileoverview Dropzone file uploader component with react-hook-form integration.
 * Exports DropzoneFileUploader; uses FileCard and FileListItem for display modes.
 */
import React, {
  useState,
  memo,
  useCallback,
  useMemo
} from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import IconMC from './IconMC';
import PreviewFile from './PreviewFile';

/**
 * Resolves react-hook-form / Yup errors for array fields (e.g. attachment[0].max-size).
 * @param {Object|undefined} errors - formState.errors
 * @param {string} fieldName
 * @returns {string|undefined}
 */
function getFieldErrorMessage(errors, fieldName) {
  const fieldError = errors?.[fieldName];
  if (!fieldError) return undefined;
  if (typeof fieldError.message === 'string' && fieldError.message) return fieldError.message;
  if (Array.isArray(fieldError)) {
    const nested = fieldError.find((item) => item?.message);
    if (nested?.message) return nested.message;
  }
  if (typeof fieldError === 'object') {
    const nested = Object.values(fieldError).find((item) => item?.message);
    if (nested?.message) return nested.message;
  }
  return undefined;
}

/**
 * Memoized card-style file item with remove button and custom preview.
 * @param {Object} props
 * @param {File} props.file - The file object to display
 * @param {number} props.index - Index of the file in the list
 * @param {function(number): void} props.onRemove - Called with index when remove is clicked
 * @param {boolean} [props.disabled=false] - Whether the remove action is disabled
 * @param {string} [props.fileContainerClassName=''] - Extra CSS classes for the card container
 * @param {function(File): React.ReactNode} props.renderPreview - Renders the file preview
 * @returns {React.ReactElement}
 */
const FileCard = memo(({
  file,
  index,
  onRemove,
  disabled,
  fileContainerClassName,
  renderPreview
}) => {
  const handleRemove = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    onRemove(index);
  }, [onRemove, index]);

  return (
    <div
      className={`tw-relative tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-shadow-sm tw-w-32 tw-h-32 tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden ${fileContainerClassName}`}
    >
      <div
        role="button"
        tabIndex={0}
        className={
          `tw-absolute tw-top-2 tw-right-2 tw-z-10 tw-text-red-500 tw-bg-red-100 tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer hover:tw-bg-red-200 tw-transition-colors ${disabled ? 'tw-cursor-not-allowed tw-opacity-50' : ''}`
        }
        onClick={handleRemove}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { handleRemove(e); } }}
      >
        <IconMC name="trash-2-outline" size="base" />
      </div>
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-w-full tw-p-2">
        {renderPreview(file)}
      </div>
    </div>
  );
});

/**
 * Memoized list-style file item with remove button and inline preview.
 * @param {Object} props
 * @param {File} props.file - The file object to display
 * @param {number} props.index - Index of the file in the list
 * @param {function(number): void} props.onRemove - Called with index when remove is clicked
 * @param {boolean} [props.disabled=false] - Whether the remove action is disabled
 * @param {string} [props.fileContainerClassName=''] - Extra CSS classes for the row container
 * @param {function(File, boolean): React.ReactNode} props.renderPreview - Renders the file preview (second arg: simplePdf)
 * @returns {React.ReactElement}
 */
const FileListItem = memo(({
  file,
  index,
  onRemove,
  disabled,
  fileContainerClassName,
  renderPreview
}) => {
  const handleRemove = useCallback((e) => {
    if (disabled) return;
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    onRemove(index);
  }, [onRemove, index, disabled]);

  return (
    <div className={`tw-w-full tw-px-0 tw-flex tw-justify-between tw-items-center tw-py-2 ${fileContainerClassName}`}>
      <span className="tw-flex tw-items-center tw-flex-1 tw-min-w-0">
        <div className="tw-mr-2 tw-flex tw-items-center tw-justify-center" style={{ width: 40, height: 40 }}>
          {renderPreview(file, true)}
        </div>
        <span className="tw-text-gray-800 tw-font-medium tw-text-sm tw-leading-6 tw-truncate tw-flex-1 tw-min-w-0">
          {file?.name || file}
        </span>
      </span>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={
          `tw-text-red-500 tw-bg-red-100 tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-cursor-pointer hover:tw-bg-red-200 tw-transition-colors tw-ml-3 tw-flex-shrink-0 ${disabled ? 'tw-cursor-not-allowed tw-opacity-50' : ''}`
        }
        onClick={handleRemove}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { handleRemove(e); } }}
      >
        <IconMC name="trash-2-outline" size="base" />
      </div>
    </div>
  );
});

/**
 * Dropzone file uploader with optional react-hook-form integration.
 * Supports card or list display, max files/size, accept types, and custom previews.
 *
 * @param {Object} props
 * @param {string} [props.id] - Fallback field identifier when name is not provided
 * @param {string} [props.name] - Form field name (used with useFormContext)
 * @param {function(File[]): void} [props.onFileSelect] - Called when files are added
 * @param {function(number): void} [props.onFileRemove] - Called when a file is removed (receives index)
 * @param {File|File[]} [props.defaultValue] - Initial file(s) when not controlled by form
 * @param {number} [props.maxSize] - Max file size in bytes
 * @param {string} [props.placeholder=''] - Placeholder text in the dropzone
 * @param {string} [props.className=''] - Extra CSS classes for the wrapper
 * @param {string} [props.accept] - Accept string (e.g. "image/*,.pdf" or "image/jpeg")
 * @param {number} [props.maxFiles=1] - Maximum number of files allowed
 * @param {boolean} [props.disabled=false] - Disables the dropzone and remove actions
 * @param {string} [props.fileContainerClassName=''] - Extra CSS classes for each file card/list item
 * @param {'list'|'card'} [props.displayMode='list'] - 'list' for compact rows, 'card' for grid cards
 * @returns {React.ReactElement}
 */
const DropzoneFileUploader = memo((props) => {
  const {
    id,
    onFileSelect,
    onFileRemove,
    defaultValue,
    name,
    maxSize,
    placeholder = "",
    className = '',
    accept,
    maxFiles = 1,
    disabled = false,
    fileContainerClassName = '',
    displayMode = 'list'
  } = props;

  const { t } = useTranslation();
  const controlForm = useFormContext();
  const fieldName = useMemo(() => name || id, [name, id]);

  // Estado local solo si no hay formulario
  const initialFiles = useMemo(() => {
    if (defaultValue) return Array.isArray(defaultValue) ? defaultValue : [defaultValue];
    return [];
  }, [defaultValue]);

  const [localFiles, setLocalFiles] = useState(initialFiles);
  const [maxFilesExceededMessage, setMaxFilesExceededMessage] = useState('');

  // Handler de drop memoizado
  const onDrop = useCallback((files, field = null, setFiles = setLocalFiles, value = localFiles) => {
    if (disabled) return;

    if (!files || files.length === 0) return;

    let newFiles = [...(value || [])];

    files.forEach((file) => {
      if (!newFiles.some((f) => f.name === file.name && f.size === file.size)) {
        newFiles.push(file);
      }
    });

    if (newFiles.length > maxFiles) {
      newFiles = newFiles.slice(0, maxFiles);
    }

    setFiles(newFiles);
    field?.onChange?.(newFiles);
    onFileSelect?.(newFiles);
  }, [disabled, maxFiles, onFileSelect, localFiles, setLocalFiles]);

  // Handler de remove memoizado
  const removeFile = useCallback((index, field = null, setFiles = setLocalFiles, value = localFiles) => {
    if (disabled) return;

    const newFiles = (value || []).filter((_, i) => i !== index);

    setFiles(newFiles);
    field?.onChange?.(newFiles);
    onFileRemove?.(index);
    if (newFiles.length <= maxFiles) {
      setMaxFilesExceededMessage('');
      if (controlForm?.clearErrors && fieldName) controlForm.clearErrors(fieldName);
    }
  }, [disabled, onFileRemove, localFiles, setLocalFiles, maxFiles, controlForm, fieldName]);

  // Configuración de dropzone memoizada
  const getDropzoneConfig = useCallback((value = localFiles, field = null, setFiles = setLocalFiles) => {
    // Configurar accept correctamente
    let acceptConfig; // undefined = acepta todos los formatos

    if (accept) {
      if (accept.includes(',')) {
        // Múltiples tipos: "image/*,.pdf"
        const types = accept.split(',').map((ttx) => ttx.trim());
        acceptConfig = {};
        types.forEach((type) => {
          // Corregir MIME types comunes mal formados
          let correctedType = type;
          if (type === 'image/jpg') {
            correctedType = 'image/jpeg';
          }

          if (correctedType.startsWith('.')) {
            // Extensión de archivo
            acceptConfig[correctedType] = [];
          } else {
            // MIME type
            acceptConfig[correctedType] = [];
          }
        });
      } else {
        // Un solo tipo
        let correctedAccept = accept;
        if (accept === 'image/jpg') {
          correctedAccept = 'image/jpeg';
        }
        acceptConfig = { [correctedAccept]: [] };
      }
    }
    // Si accept no está definido, acceptConfig permanece undefined = acepta todos

    return {
      onDrop: (acceptedFiles, rejectedFiles) => {
        const slotsLeft = maxFiles - (value?.length || 0);
        const totalSelected = (value?.length || 0) + (acceptedFiles?.length || 0);
        const hasTooMany = totalSelected > maxFiles;
        if (hasTooMany && slotsLeft > 0) {
          const toAccept = acceptedFiles.slice(0, slotsLeft);
          onDrop(toAccept, field, setFiles, value);
          const message = t('file_uploader.maxFiles', { maxFiles });
          setMaxFilesExceededMessage(message);
          if (controlForm?.setError && fieldName) {
            controlForm.setError(fieldName, { type: 'maxFiles', message });
          }
        } else if (hasTooMany && slotsLeft <= 0) {
          const message = t('file_uploader.maxFiles', { maxFiles });
          setMaxFilesExceededMessage(message);
          if (controlForm?.setError && fieldName) {
            controlForm.setError(fieldName, { type: 'maxFiles', message });
          }
        } else {
          onDrop(acceptedFiles, field, setFiles, value);
          const hasInvalidType = rejectedFiles?.some(
            (r) => r.errors?.some((e) => e.code === 'file-invalid-type')
          );
          const hasTooLarge = rejectedFiles?.some(
            (r) => r.errors?.some((e) => e.code === 'file-too-large')
          );
          if (hasInvalidType) {
            setMaxFilesExceededMessage(t('file_uploader.invalidFileType'));
          } else if (hasTooLarge) {
            const maxFileSizeMb = maxSize ? Math.round(maxSize / (1024 * 1024)) : 0;
            setMaxFilesExceededMessage(t('file_uploader.maxFileSize', { maxFileSize: maxFileSizeMb }));
          } else {
            setMaxFilesExceededMessage('');
          }
          if (rejectedFiles?.length > 0 && controlForm?.clearErrors && fieldName) {
            controlForm.clearErrors(fieldName);
          }
        }
      },
      accept: acceptConfig,
      maxFiles: 0,
      maxSize,
      disabled: disabled || (value?.length || 0) >= maxFiles,
      multiple: maxFiles > 1
    };
  }, [onDrop, accept, maxFiles, maxSize, disabled, localFiles, setLocalFiles, controlForm, fieldName, t]);

  // Clases CSS memoizadas
  const dropzoneClasses = useMemo(() => ({
    base: `tw-flex tw-h-32 tw-bg-white tw-border tw-border-dashed tw-border-gray-400 
           tw-rounded-xl tw-items-center tw-justify-center tw-cursor-pointer tw-text-gray-400
           hover:tw-border-gray-500 hover:tw-text-gray-500 tw-transition-colors`,
    active: 'tw-border-blue-400 tw-text-blue-400 tw-bg-blue-50',
    disabled: 'tw-cursor-not-allowed tw-opacity-50'
  }), []);

  // Error message memoizado (supports nested array field errors from Yup)
  const errorMessage = useMemo(
    () => getFieldErrorMessage(controlForm?.formState?.errors, fieldName),
    [controlForm?.formState?.errors, fieldName]
  );

  // Render preview memoizado
  const renderPreview = useCallback((file, simplePdf = false) => {
    if (!file || !(file instanceof File)) return null;

    return (
      <PreviewFile
        simplePdf={simplePdf}
        selectedFile={file}
        containerStyle={{
          minWidth: 'auto',
          minHeight: 'auto',
          borderRadius: '6px',
          overflow: 'hidden'
        }}
      />
    );
  }, []);

  // Render selected files optimizado
  const renderSelectedFiles = useCallback((field, setFiles, value) => {
    if (!value?.length) return null;

    const handleRemove = (index) => removeFile(index, field, setFiles, value);

    if (displayMode === 'card') {
      return (
        <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-3">
          {value.map((file, idx) => (
            <FileCard
              // eslint-disable-next-line react/no-array-index-key
              key={`${file.name}-${file.size}-${idx}`}
              file={file}
              index={idx}
              onRemove={handleRemove}
              disabled={disabled}
              fileContainerClassName={fileContainerClassName}
              renderPreview={renderPreview}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="tw-mt-2 tw-space-y-1">
        {value.map((file, idx) => (
          <FileListItem
            // eslint-disable-next-line react/no-array-index-key
            key={`${file.name}-${file.size}-${idx}`}
            file={file}
            index={idx}
            onRemove={handleRemove}
            disabled={disabled}
            fileContainerClassName={fileContainerClassName}
            renderPreview={renderPreview}
          />
        ))}
      </div>
    );
  }, [displayMode, disabled, fileContainerClassName, renderPreview, removeFile]);

  // Render dropzone content optimizado
  const dropzoneContent = useCallback((field, value, setFiles) => {
    const fileCount = value?.length || 0;
    const showDropzone = fileCount < maxFiles;

    const dropzoneConfig = getDropzoneConfig(value, field, setFiles);
    const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneConfig);

    const dropzoneClassName = useMemo(() => {
      let classes = dropzoneClasses.base;
      if (isDragActive) classes += ` ${dropzoneClasses.active}`;
      if (disabled) classes += ` ${dropzoneClasses.disabled}`;
      return classes;
    }, [isDragActive]);

    return (
      <div className={`tw-w-full ${className}`}>
        {showDropzone && (
          <div {...getRootProps()} className={dropzoneClassName}>
            <input {...getInputProps()} />
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-px-4">
              <span className="tw-text-center">
                <i className="mi-attach-fill tw-text-2xl"></i>
              </span>
              <p className="tw-text-sm tw-mt-2 tw-text-gray-400">
                {placeholder}
              </p>
            </div>
          </div>
        )}

        {fileCount > 0 && renderSelectedFiles(field, setFiles, value)}

        {(maxFilesExceededMessage || errorMessage) && (
          <p className="tw-text-xs tw-text-red-500 tw-mt-1">
            {maxFilesExceededMessage || errorMessage}
          </p>
        )}
      </div>
    );
  }, [
    className,
    maxFiles,
    disabled,
    placeholder,
    renderSelectedFiles,
    getDropzoneConfig,
    dropzoneClasses,
    errorMessage,
    maxFilesExceededMessage
  ]);

  // Valor por defecto del controlador memoizado
  const controllerDefaultValue = useMemo(() => {
    if (!defaultValue) return [];
    if (Array.isArray(defaultValue)) return defaultValue;
    return [defaultValue];
  }, [defaultValue]);

  if (!controlForm?.control) {
    // Flujo sin formulario: usar estado local
    return dropzoneContent(null, localFiles, setLocalFiles);
  }

  return (
    <Controller
      name={fieldName}
      control={controlForm.control}
      defaultValue={controllerDefaultValue}
      render={({ field }) => dropzoneContent(field, field.value || [], field.onChange || (() => { }))}
    />
  );
});

// Establecer displayName para mejor debugging
DropzoneFileUploader.displayName = 'DropzoneFileUploader';

export default DropzoneFileUploader;
